/**
 * Firebase Configuration
 *
 * This file initializes Firebase with your project credentials.
 * All sensitive values should be stored in environment variables.
 *
 * Required environment variables (set in .env):
 * - VITE_FIREBASE_API_KEY
 * - VITE_FIREBASE_AUTH_DOMAIN
 * - VITE_FIREBASE_PROJECT_ID
 * - VITE_FIREBASE_STORAGE_BUCKET
 * - VITE_FIREBASE_MESSAGING_SENDER_ID
 * - VITE_FIREBASE_APP_ID
 *
 * Firebase Console Setup:
 * 1. Go to console.firebase.google.com
 * 2. Create a new project or select existing
 * 3. Enable Authentication > Sign-in methods:
 *    - Email/Password (enable)
 *    - Google (enable)
 *    - Apple (enable - requires Apple Developer account)
 *    - Microsoft (enable - requires Azure AD app)
 *    - Yahoo (enable)
 * 4. Enable Multi-factor Authentication in Authentication > Settings
 * 5. Copy your config values to .env file
 */

import { initializeApp } from 'firebase/app';
import { getAnalytics, isSupported } from 'firebase/analytics';
import {
  getAuth,
  GoogleAuthProvider,
  OAuthProvider,
  browserLocalPersistence,
  indexedDBLocalPersistence,
  setPersistence,
  signInWithPopup,
  signInWithCredential,
  getRedirectResult,
} from 'firebase/auth';
import type { AuthProvider as FirebaseAuthProvider, UserCredential } from 'firebase/auth';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate configuration
const requiredFields = ['apiKey', 'authDomain', 'projectId', 'appId'];
const missingFields = requiredFields.filter(
  (field) => !firebaseConfig[field as keyof typeof firebaseConfig]
);

// Check if we're in a valid configuration state
const isConfigValid = missingFields.length === 0;

if (!isConfigValid) {
  if (import.meta.env.PROD) {
    console.error(
      `Missing Firebase configuration: ${missingFields.join(', ')}. ` +
      'Please set the required VITE_FIREBASE_* environment variables.'
    );
  } else {
    console.warn(
      `Firebase not fully configured (missing: ${missingFields.join(', ')}). ` +
      'Auth features will be unavailable. This is expected in test/dev without Firebase setup.'
    );
  }
}

// Initialize Firebase only if config is valid, otherwise use a dummy config for dev
const app = isConfigValid
  ? initializeApp(firebaseConfig)
  : initializeApp({
      apiKey: 'demo-api-key',
      authDomain: 'demo.firebaseapp.com',
      projectId: 'demo-project',
      appId: '1:demo:web:demo',
    });

// Initialize Firebase Analytics (only in browser, not SSR)
export let analytics: ReturnType<typeof getAnalytics> | null = null;
isSupported().then((supported) => {
  if (supported) {
    analytics = getAnalytics(app);
  }
});

// Initialize Firebase Authentication
export const auth = getAuth(app);

// =============================================================================
// Platform Detection for Mobile Auth
// =============================================================================

/**
 * Check if running as a native mobile app (iOS or Android via Capacitor)
 */
export const isNativePlatform = (): boolean => Capacitor.isNativePlatform();

/**
 * Check if running on iOS
 */
export const isIOS = (): boolean => Capacitor.getPlatform() === 'ios';

/**
 * Check if running on Android
 */
export const isAndroid = (): boolean => Capacitor.getPlatform() === 'android';

// Set persistence based on platform
// Native apps use indexedDB for better compatibility
// Web uses browserLocal for broader support
const persistence = isNativePlatform() ? indexedDBLocalPersistence : browserLocalPersistence;
setPersistence(auth, persistence).catch((error) => {
  console.error('Error setting auth persistence:', error);
});

// =============================================================================
// OAuth Providers Configuration
// =============================================================================

/**
 * Google OAuth Provider
 * - Most common OAuth provider
 * - Works out of the box with Firebase
 */
export const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');
// Force account selection even if user is already signed in to Google
googleProvider.setCustomParameters({
  prompt: 'select_account',
});

/**
 * Apple OAuth Provider
 * Requires:
 * - Apple Developer account ($99/year)
 * - Services ID configured in Apple Developer Console
 * - Domain verification
 */
export const appleProvider = new OAuthProvider('apple.com');
appleProvider.addScope('email');
appleProvider.addScope('name');

/**
 * Microsoft OAuth Provider - DISABLED
 * Will be re-enabled if Windows PC app is created
 *
 * Requires:
 * - Azure AD app registration
 * - Redirect URI configured in Azure Portal
 */
// export const microsoftProvider = new OAuthProvider('microsoft.com');
// microsoftProvider.addScope('email');
// microsoftProvider.addScope('profile');
// microsoftProvider.addScope('openid');
// // Allow personal Microsoft accounts (not just work/school)
// microsoftProvider.setCustomParameters({
//   prompt: 'select_account',
//   tenant: 'common', // Allows both personal and work accounts
// });

/**
 * Yahoo OAuth Provider - DISABLED
 * Not needed for current mobile/web strategy
 */
// export const yahooProvider = new OAuthProvider('yahoo.com');
// yahooProvider.addScope('email');
// yahooProvider.addScope('profile');

// =============================================================================
// Helper Types
// =============================================================================

export type OAuthProviderType = 'google' | 'apple'; // 'microsoft' | 'yahoo' removed

export const oauthProviders: Record<OAuthProviderType, {
  provider: GoogleAuthProvider | OAuthProvider;
  name: string;
  icon: string;
  bgColor: string;
  textColor: string;
}> = {
  google: {
    provider: googleProvider,
    name: 'Google',
    icon: 'G',
    bgColor: 'bg-white hover:bg-gray-50',
    textColor: 'text-gray-700',
  },
  apple: {
    provider: appleProvider,
    name: 'Apple',
    icon: '',
    bgColor: 'bg-black hover:bg-gray-900',
    textColor: 'text-white',
  },
  // Microsoft and Yahoo removed - will be re-added if Windows PC app is created
  // microsoft: {
  //   provider: microsoftProvider,
  //   name: 'Microsoft',
  //   icon: '⊞',
  //   bgColor: 'bg-[#2F2F2F] hover:bg-[#1F1F1F]',
  //   textColor: 'text-white',
  // },
  // yahoo: {
  //   provider: yahooProvider,
  //   name: 'Yahoo',
  //   icon: 'Y!',
  //   bgColor: 'bg-[#720E9E] hover:bg-[#5A0B7D]',
  //   textColor: 'text-white',
  // },
};

// =============================================================================
// Mobile-Aware Authentication Helpers
// =============================================================================

/**
 * Sign in with OAuth provider using the appropriate method for the platform.
 *
 * - On native mobile: Uses Capacitor Firebase Authentication plugin
 * - On web: Uses Firebase popup (better UX, doesn't leave the page)
 *
 * @param provider - The OAuth provider to use
 * @returns UserCredential from the auth flow
 */
export const signInWithProvider = async (
  provider: FirebaseAuthProvider
): Promise<UserCredential> => {
  if (isNativePlatform()) {
    // On native mobile, use the Capacitor Firebase Authentication plugin
    // With skipNativeAuth: true, this opens in Chrome Custom Tabs
    const providerId = (provider as GoogleAuthProvider | OAuthProvider).providerId;

    try {
      let result;
      if (providerId === 'google.com') {
        result = await FirebaseAuthentication.signInWithGoogle();
      } else if (providerId === 'apple.com') {
        result = await FirebaseAuthentication.signInWithApple();
      // } else if (providerId === 'microsoft.com') {
      //   result = await FirebaseAuthentication.signInWithMicrosoft();
      // } else if (providerId === 'yahoo.com') {
      //   result = await FirebaseAuthentication.signInWithYahoo();
      } else {
        throw new Error(`Unsupported OAuth provider: ${providerId}`);
      }

      // With skipNativeAuth: true, the user is signed in via web SDK automatically
      // The result.user will be available if sign-in succeeded
      if (result.user) {
        // Wait a moment for the auth state to propagate
        await new Promise(resolve => setTimeout(resolve, 500));
        const currentUser = auth.currentUser;
        if (currentUser) {
          return { user: currentUser } as UserCredential;
        }
      }

      // If we got credentials, sign in with them
      if (result.credential?.idToken) {
        const credential = GoogleAuthProvider.credential(
          result.credential.idToken,
          result.credential.accessToken
        );
        return signInWithCredential(auth, credential);
      }

      throw new Error('Sign-in completed but no user returned');
    } catch (error) {
      console.error('Native sign-in failed:', error);
      throw error;
    }
  } else {
    // On web, use popup for better UX
    return signInWithPopup(auth, provider);
  }
};

/**
 * Handle OAuth redirect result on app load.
 * Call this once when the app starts to complete any pending auth redirects.
 *
 * @returns UserCredential if redirect completed, null otherwise
 */
export const handleAuthRedirect = async (): Promise<UserCredential | null> => {
  try {
    const result = await getRedirectResult(auth);
    return result;
  } catch (error) {
    console.error('Error handling auth redirect:', error);
    throw error;
  }
};

/**
 * Get the appropriate provider for Sign in with Apple.
 * Note: Sign in with Apple is REQUIRED for iOS apps that offer social login.
 *
 * Apple requirements:
 * - Must be the first/prominent option if you offer any social login
 * - Must request email and name scopes
 */
export const getAppleProviderForPlatform = (): OAuthProvider => {
  const provider = new OAuthProvider('apple.com');
  provider.addScope('email');
  provider.addScope('name');

  // On native iOS, we might need additional configuration
  if (isIOS()) {
    // iOS native Sign in with Apple uses different flow
    // The Apple capability must be added in Xcode
    provider.setCustomParameters({
      locale: 'en',
    });
  }

  return provider;
};

export default app;
